import { z } from "zod";

// ===== API response shape (validated at the edge with Zod) =====

/** Mirrors the API's ReleaseNoteKind enum, serialised by name. */
export const releaseNoteKindSchema = z.enum(["UserFacing", "Technical"]);

export type ReleaseNoteKind = z.infer<typeof releaseNoteKindSchema>;

export const releaseNoteResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** ISO date-only, e.g. "2026-09-10". */
  releaseDate: z.string(),
  /** Markdown body. */
  body: z.string(),
  /** Absent on notes an older API wrote; they are user-facing. */
  kind: releaseNoteKindSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});

export type ReleaseNoteResponse = z.infer<typeof releaseNoteResponseSchema>;

// ===== Parsed domain type (frontend use) =====

export interface ReleaseNote {
  id: string;
  title: string;
  releaseDate: Date;
  body: string;
  /**
   * Nothing a user can see (CI, dependencies, infrastructure). The changelog
   * hides these unless the user asks for them, and they never count as unread.
   */
  isTechnical: boolean;
  createdAt: Date;
  updatedAt: Date | null;
}

/**
 * Parses "yyyy-MM-dd" into a local Date at midnight. Building it from the parts
 * (rather than `new Date(str)`, which is parsed as UTC) keeps the month/year the
 * user picked stable regardless of the viewer's timezone.
 */
const parseIsoDateOnly = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
};

export const parseReleaseNoteResponse = (
  raw: ReleaseNoteResponse
): ReleaseNote => {
  const parsed = releaseNoteResponseSchema.parse(raw);
  return {
    id: parsed.id,
    title: parsed.title,
    releaseDate: parseIsoDateOnly(parsed.releaseDate),
    body: parsed.body,
    isTechnical: parsed.kind === "Technical",
    createdAt: new Date(parsed.createdAt),
    updatedAt: parsed.updatedAt ? new Date(parsed.updatedAt) : null,
  };
};
